import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { api } from '../api/client';
import {
  ChangeView,
  HeatmapLayer,
  getMarkerIcon,
  getRiskLabel,
  getRiskBadgeClass,
} from '../components/MapHelpers';
import { StateSourceBadge } from '../components/StateSourceBadge';

const PAGE_SIZE = 50;

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name A-Z' },
  { value: 'name_desc', label: 'Name Z-A' },
  { value: 'newest_conviction', label: 'Newest Conviction' },
  { value: 'age_asc', label: 'Age (youngest)' },
  { value: 'age_desc', label: 'Age (oldest)' },
];

function SearchPage() {
  // Search mode: 'name' or 'zip'
  const [searchMode, setSearchMode] = useState('name');

  // Name search fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // ZIP search field
  const [zipCode, setZipCode] = useState('');

  // Results
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchLabel, setSearchLabel] = useState('');

  // Map
  const [mapCenter, setMapCenter] = useState([39.8283, -98.5795]);
  const [mapZoom, setMapZoom] = useState(4);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [satelliteView, setSatelliteView] = useState(false);

  // View mode
  const [viewMode, setViewMode] = useState('list');

  // Filters
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [riskLevelFilter, setRiskLevelFilter] = useState('');
  const [sexFilter, setSexFilter] = useState('');
  const [sortBy, setSortBy] = useState('name_asc');

  const searchIdRef = useRef(0);
  const isInitialMount = useRef(true);

  const executeSearch = useCallback(async (page = 1) => {
    let params = {
      risk_level: riskLevelFilter || undefined,
      sex: sexFilter || undefined,
      sort: sortBy,
      page,
      page_size: PAGE_SIZE,
    };

    if (searchMode === 'name') {
      const first = firstName.trim();
      const last = lastName.trim();
      if (!first && !last) {
        setError('Please enter at least a first or last name.');
        return;
      }
      if (first) params.firstName = first;
      if (last) params.lastName = last;
      setSearchLabel([first, last].filter(Boolean).join(' '));
    } else {
      const zip = zipCode.trim();
      if (!zip || !/^\d{5}$/.test(zip)) {
        setError('Please enter a valid 5-digit ZIP code.');
        return;
      }
      params.zipCode = zip;
      setSearchLabel(zip);
    }

    const thisSearchId = ++searchIdRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.offenders.search(params);
      if (thisSearchId !== searchIdRef.current) return;

      const data = response.data;
      const searchResults = data.results || [];
      setResults(searchResults);
      setPagination(data.pagination || null);
      setHasSearched(true);

      if (searchResults.length > 0) {
        const first = searchResults.find((r) => r.latitude && r.longitude);
        if (first) {
          setMapCenter([parseFloat(first.latitude), parseFloat(first.longitude)]);
          setMapZoom(12);
        }
      }
    } catch (err) {
      if (thisSearchId !== searchIdRef.current) return;
      setError(err.response?.data?.error || err.message || 'Search failed.');
      setResults([]);
      setPagination(null);
    } finally {
      if (thisSearchId === searchIdRef.current) setIsLoading(false);
    }
  }, [searchMode, firstName, lastName, zipCode, riskLevelFilter, sexFilter, sortBy]);

  const handleSearch = (e) => {
    e.preventDefault();
    executeSearch(1);
  };

  const switchMode = (mode) => {
    if (mode === searchMode) return;
    setSearchMode(mode);
    setResults([]);
    setPagination(null);
    setError(null);
    setHasSearched(false);
    setSearchLabel('');
  };

  const goToPage = (page) => {
    executeSearch(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const canSubmit = searchMode === 'name'
    ? (firstName.trim() || lastName.trim()) && !isLoading
    : zipCode.length === 5 && !isLoading;

  const mapResults = useMemo(() => results.filter((r) => r.latitude && r.longitude), [results]);

  const heatmapPoints = useMemo(() => {
    return mapResults.map((r) => {
      const riskLabel = getRiskLabel(r.risk_level, r.tier_level);
      const intensity = riskLabel === 'High' ? 1.0 : riskLabel === 'Medium' ? 0.6 : 0.3;
      return [parseFloat(r.latitude), parseFloat(r.longitude), intensity];
    });
  }, [mapResults]);

  const formatAddress = (result) => {
    const parts = [result.address_line1, result.city, result.state_id, result.zip_code].filter(Boolean);
    return parts.join(', ') || 'Address not available';
  };

  const pageNumbers = useMemo(() => {
    if (!pagination || pagination.total_pages <= 1) return [];
    const total = pagination.total_pages;
    const current = pagination.page;
    const pages = [];
    pages.push(1);
    const start = Math.max(2, current - 2);
    const end = Math.min(total - 1, current + 2);
    if (start > 2) pages.push('...');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total - 1) pages.push('...');
    if (total > 1) pages.push(total);
    return pages;
  }, [pagination]);

  const activeFilterCount = [riskLevelFilter, sexFilter].filter(Boolean).length;

  // Re-run search when sort/filter values change (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (hasSearched) {
      executeSearch(1);
    }
  }, [sortBy, riskLevelFilter, sexFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-slate-800 text-white py-4 sm:py-8 px-3 sm:px-4 search-results-container">
      <Helmet>
        <title>Search Sex Offender Registry — CommunityGuardAPI</title>
        <link rel="canonical" href="https://communityguardapi.com/search" />
        <meta name="description" content="Search registered sex offenders by name or ZIP code across all 50 US states." />
        <meta property="og:title" content="Search Sex Offender Registry — CommunityGuardAPI" />
        <meta property="og:description" content="Search registered sex offenders by name or ZIP code across all 50 US states." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://communityguardapi.com/search" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl sm:text-4xl font-bold mb-2 text-center">Search Registry</h1>
        <p className="text-slate-400 text-xs sm:text-sm text-center mb-4 sm:mb-6">Search by name or ZIP code</p>

        {/* Mode Tabs */}
        <div className="flex max-w-md mx-auto mb-4 bg-slate-700/50 rounded-lg p-1">
          {[
            { mode: 'name', label: 'Search by Name' },
            { mode: 'zip', label: 'Search by ZIP Code' },
          ].map((tab) => (
            <button
              key={tab.mode}
              onClick={() => switchMode(tab.mode)}
              className={`flex-1 py-2 rounded-md text-xs sm:text-sm font-semibold transition-all ${
                searchMode === tab.mode
                  ? 'bg-emerald-500 text-white shadow'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-4 sm:mb-6 max-w-md mx-auto">
          {searchMode === 'name' ? (
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  className="flex-1 min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg bg-slate-700 text-white text-sm sm:text-base placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                  disabled={isLoading}
                  autoComplete="off"
                />
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  className="flex-1 min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg bg-slate-700 text-white text-sm sm:text-base placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                  disabled={isLoading}
                  autoComplete="off"
                />
              </div>
              <button
                type="submit"
                className="w-full py-2.5 sm:py-3 text-sm sm:text-base font-semibold rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!canSubmit}
              >
                {isLoading ? 'Searching...' : 'Search by Name'}
              </button>
            </div>
          ) : (
            <div className="flex gap-2 sm:gap-3">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={5}
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="Enter ZIP code (e.g. 10001)"
                className="flex-grow min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg bg-slate-700 text-white text-sm sm:text-base placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 tabular-nums tracking-wider text-center font-semibold"
                disabled={isLoading}
              />
              <button
                type="submit"
                className="flex-shrink-0 px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!canSubmit}
              >
                {isLoading ? '...' : 'Search'}
              </button>
            </div>
          )}
        </form>

        {/* Controls Panel */}
        {hasSearched && (
          <div className="mb-4 sm:mb-6 max-w-4xl mx-auto">
            <div className="bg-slate-800/80 backdrop-blur-sm border border-slate-700 rounded-xl p-3 sm:p-4 space-y-2.5">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="flex-1 min-w-0 px-2 sm:px-3 py-2 rounded-lg bg-slate-700/60 text-white text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  className={`shrink-0 px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                    filtersOpen || activeFilterCount > 0
                      ? 'bg-emerald-500/90 text-white'
                      : 'bg-slate-700/60 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                  }`}
                >
                  Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                </button>

                {activeFilterCount > 0 && (
                  <button type="button" onClick={() => { setRiskLevelFilter(''); setSexFilter(''); }} className="shrink-0 px-2 py-2 text-xs text-red-400 hover:text-red-300 font-medium">
                    Clear
                  </button>
                )}
              </div>

              {/* Map toggles */}
              {mapResults.length > 0 && (
                <div className="flex items-center gap-1.5 sm:gap-2">
                  {[
                    { active: showHeatmap, toggle: () => setShowHeatmap(!showHeatmap), label: 'Heatmap' },
                    { active: satelliteView, toggle: () => setSatelliteView(!satelliteView), label: satelliteView ? 'Street' : 'Satellite' },
                  ].map((btn, i) => (
                    <button
                      key={i}
                      onClick={btn.toggle}
                      className={`flex-1 py-1.5 sm:py-2 rounded-lg text-[11px] sm:text-xs font-semibold transition-all ${
                        btn.active ? 'bg-emerald-500/90 text-white' : 'bg-slate-700/60 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                      }`}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Filter Panel */}
            {filtersOpen && (
              <div className="mt-2 bg-slate-700/80 backdrop-blur-sm border border-slate-600 rounded-xl p-3 sm:p-4 shadow-lg">
                <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Risk Level</label>
                    <select value={riskLevelFilter} onChange={(e) => setRiskLevelFilter(e.target.value)}
                      className="w-full px-2.5 py-2 rounded-lg bg-slate-600/80 text-white text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500">
                      <option value="">Any</option>
                      <option value="High">High</option>
                      <option value="Moderate Risk">Moderate</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Sex</label>
                    <select value={sexFilter} onChange={(e) => setSexFilter(e.target.value)}
                      className="w-full px-2.5 py-2 rounded-lg bg-slate-600/80 text-white text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500">
                      <option value="">Any</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Result count */}
        {pagination && (
          <div className="mb-3 text-center text-slate-400 text-xs sm:text-sm">
            {pagination.total_count === 0
              ? `No results for "${searchLabel}"`
              : <>
                  <span className="font-bold text-white tabular-nums">{pagination.total_count.toLocaleString()}</span> results for <span className="text-emerald-400 font-semibold">"{searchLabel}"</span>
                  <span className="text-slate-500 mx-1">&middot;</span>
                  page {pagination.page} of {Math.ceil(pagination.total_count / pagination.page_size)}
                </>
            }
          </div>
        )}

        {/* Map */}
        {mapResults.length > 0 && (
          <div className="mb-6 sm:mb-8 w-full h-[300px] sm:h-[450px] md:h-[600px] rounded-lg overflow-hidden shadow-xl border border-slate-700">
            <MapContainer center={mapCenter} zoom={mapZoom} scrollWheelZoom={true} style={{ height: '100%', width: '100%' }}>
              <ChangeView center={mapCenter} zoom={mapZoom} />
              <TileLayer
                key={satelliteView ? 'sat' : 'street'}
                attribution={satelliteView ? '&copy; Esri' : '&copy; OpenStreetMap'}
                url={satelliteView
                  ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
                  : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'}
              />
              {showHeatmap && heatmapPoints.length > 0 && <HeatmapLayer points={heatmapPoints} />}
              {!showHeatmap && (
                <MarkerClusterGroup chunkedLoading maxClusterRadius={50} spiderfyOnMaxZoom showCoverageOnHover={false}
                  iconCreateFunction={(cluster) => {
                    const count = cluster.getChildCount();
                    let sizeClass = 'pr-cluster-small';
                    if (count > 50) sizeClass = 'pr-cluster-large';
                    else if (count > 10) sizeClass = 'pr-cluster-medium';
                    return L.divIcon({ html: `<div><span>${count}</span></div>`, className: `pr-marker-cluster ${sizeClass}`, iconSize: L.point(40, 40) });
                  }}
                >
                  {mapResults.map((result) => {
                    const riskLabel = getRiskLabel(result.risk_level, result.tier_level);
                    const placeholderUrl = 'https://via.placeholder.com/80/CBD5E1/1E293B?text=?';
                    const imgSrc = result.offender_image_url || placeholderUrl;
                    return (
                      <Marker key={`map-${result.id}`} position={[parseFloat(result.latitude), parseFloat(result.longitude)]} icon={getMarkerIcon(result.risk_level, result.tier_level)}>
                        <Popup minWidth={220} maxWidth={280}>
                          <div className="pr-popup">
                            <img src={imgSrc} alt={result.full_name || 'Offender'} className="pr-popup-img"
                              onError={(e) => { if (e.target.src !== placeholderUrl) { e.target.onerror = null; e.target.src = placeholderUrl; } }} />
                            <strong className="pr-popup-name">{result.full_name || 'N/A'}</strong>
                            <span className={getRiskBadgeClass(riskLabel)}>{riskLabel} Risk</span>
                            <p className="pr-popup-address">{formatAddress(result)}</p>
                            <Link to={`/offenders/${result.id}`} className="pr-popup-link">View Details</Link>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MarkerClusterGroup>
              )}
            </MapContainer>
          </div>
        )}

        {/* View Mode Tabs */}
        {hasSearched && results.length > 0 && !isLoading && (
          <div className="flex max-w-xs mx-auto mb-4 bg-slate-700/50 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs sm:text-sm font-semibold transition-all ${
                viewMode === 'list'
                  ? 'bg-emerald-500 text-white shadow'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
              List
            </button>
            <button
              onClick={() => setViewMode('gallery')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs sm:text-sm font-semibold transition-all ${
                viewMode === 'gallery'
                  ? 'bg-emerald-500 text-white shadow'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
              Gallery
            </button>
          </div>
        )}

        {/* Results */}
        <div className="mt-6">
          {isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-slate-700 rounded-lg p-3 sm:p-4 shadow-md animate-pulse">
                  <div className="flex gap-3 sm:flex-col sm:items-center">
                    <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-slate-600 flex-shrink-0" />
                    <div className="flex-grow min-w-0 sm:w-full space-y-2 sm:mt-2">
                      <div className="h-4 bg-slate-600 rounded w-3/4 sm:mx-auto" />
                      <div className="h-3 bg-slate-600 rounded w-1/2 sm:mx-auto" />
                      <div className="h-3 bg-slate-600 rounded w-2/3 sm:mx-auto" />
                    </div>
                  </div>
                  <div className="mt-3 pt-2 border-t border-slate-600">
                    <div className="h-3 bg-slate-600 rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && <p className="text-red-400 text-center py-4">{error}</p>}

          {/* List View */}
          {!isLoading && !error && results.length > 0 && viewMode === 'list' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 offender-card-grid">
              {results.map((result) => {
                const placeholderUrl = 'https://via.placeholder.com/150/475569/94a3b8?text=?';
                const imageSource = result.offender_image_url || placeholderUrl;
                const offenderName = result.full_name || 'N/A';
                const riskLabel = getRiskLabel(result.risk_level, result.tier_level);

                return (
                  <div key={result.id} className="offender-card bg-slate-700 rounded-lg p-3 sm:p-4 shadow-md hover:shadow-lg transition-shadow">
                    <div className="flex gap-3 sm:flex-col sm:items-center">
                      <Link to={`/offenders/${result.id}`} className="flex-shrink-0">
                        <img src={imageSource} alt={offenderName}
                          className="w-16 h-16 sm:w-24 sm:h-24 rounded-full object-cover bg-slate-600"
                          onError={(e) => { if (e.target.src !== placeholderUrl) { e.target.onerror = null; e.target.src = placeholderUrl; } }} />
                      </Link>
                      <div className="flex-grow min-w-0 sm:text-center">
                        <Link to={`/offenders/${result.id}`} className="no-underline text-inherit">
                          <h3 className="text-sm sm:text-lg font-semibold text-white truncate">{offenderName}</h3>
                        </Link>
                        <div className="flex flex-wrap items-center gap-1 mt-1 mb-1">
                          <span className={`inline-block text-xs ${getRiskBadgeClass(riskLabel)}`}>{riskLabel} Risk</span>
                          <StateSourceBadge stateCode={result.source_state} />
                        </div>
                        <div className="text-xs sm:text-sm text-slate-300">
                          <p>{[result.age && `Age ${result.age}`, result.sex].filter(Boolean).join(' | ')}</p>
                          {result.designation && <p className="font-medium text-orange-400 truncate">{result.designation}</p>}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-slate-600 text-xs text-slate-400">
                      <p className="truncate">{formatAddress(result)}</p>
                      <Link to={`/offenders/${result.id}`} className="inline-block mt-1.5 text-emerald-400 hover:text-amber-400 text-xs font-medium transition-colors">
                        View Details &rarr;
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Gallery View */}
          {!isLoading && !error && results.length > 0 && viewMode === 'gallery' && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
              {results.map((result) => {
                const placeholderUrl = 'https://via.placeholder.com/200/475569/94a3b8?text=?';
                const imageSource = result.offender_image_url || placeholderUrl;
                const offenderName = result.full_name || 'N/A';
                const riskLabel = getRiskLabel(result.risk_level, result.tier_level);
                const riskColor = riskLabel === 'High' ? 'bg-red-500' : riskLabel === 'Medium' ? 'bg-amber-500' : 'bg-emerald-500';

                return (
                  <Link
                    key={result.id}
                    to={`/offenders/${result.id}`}
                    className="group rounded-lg overflow-hidden bg-slate-700 shadow-md hover:shadow-xl hover:ring-2 hover:ring-emerald-400 transition-all"
                  >
                    <div className="relative aspect-square">
                      <img
                        src={imageSource}
                        alt={offenderName}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => { if (e.target.src !== placeholderUrl) { e.target.onerror = null; e.target.src = placeholderUrl; } }}
                      />
                    </div>
                    {/* Always-visible info strip */}
                    <div className="px-2 py-1.5 bg-slate-800/90">
                      <p className="text-white text-xs font-semibold leading-tight truncate">{offenderName}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`inline-block w-2 h-2 rounded-full ${riskColor} flex-shrink-0`}></span>
                        <span className="text-[10px] text-slate-400 font-medium">{riskLabel} Risk</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {!isLoading && !error && hasSearched && results.length === 0 && (
            <div className="max-w-sm mx-auto text-center py-10">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-slate-700/60 flex items-center justify-center">
                <svg className="w-7 h-7 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              </div>
              <h3 className="font-heading text-lg font-semibold text-white mb-1">No results found</h3>
              <p className="text-slate-400 text-sm mb-4">No matches for "{searchLabel}"</p>
              <p className="text-slate-500 text-xs">
                {searchMode === 'name'
                  ? 'Try a different spelling, or switch to ZIP code search to find offenders by area.'
                  : 'Try a different ZIP code, or switch to name search for a specific person.'}
              </p>
            </div>
          )}
          {!isLoading && !error && !hasSearched && (
            <div className="text-center py-12 sm:py-16">
              <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-2">Search the National Registry</h3>
              <p className="text-slate-400 text-sm sm:text-base max-w-md mx-auto">
                {searchMode === 'name'
                  ? 'Enter a first name, last name, or both to search across all state registries.'
                  : 'Enter a 5-digit ZIP code to find registered offenders in that area.'}
              </p>
              <p className="text-slate-500 text-xs mt-3">Results are paginated — 50 per page</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <div className="mt-8 sm:mt-10 flex flex-wrap justify-center items-center gap-1.5 sm:gap-2">
            <button onClick={() => goToPage(pagination.page - 1)} disabled={!pagination.has_previous || isLoading}
              className="px-3 sm:px-4 py-2 rounded bg-slate-700 text-white text-xs sm:text-sm hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              Prev
            </button>
            {pageNumbers.map((p, idx) =>
              p === '...' ? (
                <span key={`ellipsis-${idx}`} className="px-1 sm:px-2 py-2 text-slate-500 text-xs sm:text-sm">...</span>
              ) : (
                <button key={p} onClick={() => goToPage(p)} disabled={isLoading}
                  className={`px-2.5 sm:px-3 py-2 rounded text-xs sm:text-sm transition-colors ${
                    p === pagination.page ? 'bg-emerald-500 text-white font-bold' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  } disabled:opacity-40 disabled:cursor-not-allowed`}>
                  {p}
                </button>
              )
            )}
            <button onClick={() => goToPage(pagination.page + 1)} disabled={!pagination.has_next || isLoading}
              className="px-3 sm:px-4 py-2 rounded bg-slate-700 text-white text-xs sm:text-sm hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchPage;
